import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import chesiLogo from '../chesi-logo.png';
import api from '../services/api';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import PrintIcon from '@mui/icons-material/Print';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import TabletIcon from '@mui/icons-material/Tablet';
import ReceiptIcon from '@mui/icons-material/Receipt';
import LabelIcon from '@mui/icons-material/Label';
import SignalCellularAltIcon from '@mui/icons-material/SignalCellularAlt';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import './Shop.css';

/** MUI fallback when CMS has no icon_url for this slug */
const FALLBACK_ICON_BY_SLUG = {
  'handheld-computers': PhoneAndroidIcon,
  'barcode-printers': PrintIcon,
  'barcode-scanners': QrCodeScannerIcon,
  'tablet-computers': TabletIcon,
  'receipt-printers': ReceiptIcon,
  'barcode-labels': LabelIcon,
  'rfid-readers': SignalCellularAltIcon,
  'autonomous-mobile-robotics': SmartToyIcon
};

function shopImageBase() {
  const base =
    process.env.REACT_APP_API_URL ||
    (process.env.NODE_ENV === 'production'
      ? 'https://chesiwms-production-1eb5.up.railway.app/api'
      : 'http://localhost:5000/api');
  return base.replace(/\/?api\/?$/i, '');
}

function mapApiProductToCard(p) {
  const origin = shopImageBase();
  const raw = p.primary_image_url;
  const image =
    raw && /^https?:\/\//i.test(raw)
      ? raw
      : raw
        ? `${origin}${raw.startsWith('/') ? '' : '/'}${raw}`
        : `https://via.placeholder.com/200x200?text=${encodeURIComponent(p.sku || 'Product')}`;
  const priceNum = Number(p.price);
  const price =
    Number.isFinite(priceNum) && priceNum > 0
      ? `KES ${priceNum.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
      : 'Request Price';
  return {
    id: p.id,
    name: p.name,
    model: p.sku || '—',
    price,
    image,
    description: p.short_description || '',
    categorySlug: p.category_slug || null
  };
}

const Shop = () => {
  const [cartCount, setCartCount] = useState(0);
  const [topCategories, setTopCategories] = useState([]);
  const [categoriesError, setCategoriesError] = useState('');
  const [brands, setBrands] = useState([]);
  const [brandsError, setBrandsError] = useState('');
  const [bestSellers, setBestSellers] = useState([]);
  const [customerFavorites, setCustomerFavorites] = useState([]);
  const [shopProductsError, setShopProductsError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.getPublicShopCategories();
        const list = Array.isArray(res?.data) ? res.data : [];
        if (!cancelled) {
          setTopCategories(list);
          setCategoriesError('');
        }
      } catch (e) {
        if (!cancelled) {
          setTopCategories([]);
          setCategoriesError(e.message || 'Could not load categories');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.getPublicShopBrands();
        const list = Array.isArray(res?.data) ? res.data : [];
        if (!cancelled) {
          setBrands(list);
          setBrandsError('');
        }
      } catch (e) {
        if (!cancelled) {
          setBrands([]);
          setBrandsError(e.message || 'Could not load brands');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setShopProductsError('');
        const res = await api.getPublicShopProducts({ limit: 36, offset: 0, featured: false });
        const rows = Array.isArray(res?.data) ? res.data : [];
        const mapped = rows.map(mapApiProductToCard);
        if (!cancelled) {
          setBestSellers(mapped.slice(0, 6));
          setCustomerFavorites(mapped.slice(6, 18));
        }
      } catch (e) {
        if (!cancelled) {
          setBestSellers([]);
          setCustomerFavorites([]);
          setShopProductsError(e.message || 'Could not load products');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const topCustomers = [
    { name: 'Google', logo: 'https://via.placeholder.com/120x60?text=Google' },
    { name: 'Amazon', logo: 'https://via.placeholder.com/120x60?text=Amazon' },
    { name: 'American Airlines', logo: 'https://via.placeholder.com/120x60?text=AA' },
    { name: 'HP', logo: 'https://via.placeholder.com/120x60?text=HP' },
    { name: 'Boeing', logo: 'https://via.placeholder.com/120x60?text=Boeing' },
    { name: 'Merck', logo: 'https://via.placeholder.com/120x60?text=Merck' },
    { name: 'Nestle', logo: 'https://via.placeholder.com/120x60?text=Nestle' }
  ];

  const ProductCard = ({ product, variant = '' }) => (
    <div className={`product-card ${variant}`.trim()}>
      <div className="product-image">
        <img src={product.image} alt={product.name} onError={(e) => { e.target.src = 'https://via.placeholder.com/200x200?text=' + product.model; }} />
      </div>
      <div className="product-info">
        <h3 className="product-name">{product.name}</h3>
        <p className="product-model">{product.model}</p>
        <p className="product-price">{product.price}</p>
        {variant === 'strip-card' ? (
          <div className="product-actions strip-actions">
            <div className="strip-inline-row">
              <label>Qty:</label>
              <input type="number" min="1" defaultValue="1" />
              <button className="btn-add-cart">Add to Cart</button>
            </div>
            <a href="#" className="link-quote">+ Add to Quote</a>
          </div>
        ) : (
          <div className="product-actions">
            <div className="quantity-selector">
              <label>Qty:</label>
              <input type="number" min="1" defaultValue="1" />
            </div>
            <button className="btn-add-cart">Add to Cart</button>
            <a href="#" className="link-quote">Request Price</a>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="shop-page">
      {/* Header */}
      <header className="shop-header">
        <div className="header-top">
          <div className="container">
            <div className="header-content">
              <Link to="/" className="logo">
                <img src={chesiLogo} alt="Chesi" className="logo-img" />
                <span className="logo-text">AIDC Products</span>
              </Link>
              <div className="header-search">
                <input 
                  type="text" 
                  placeholder="Search keyword, SKU or part #" 
                  className="search-input"
                />
                <button className="search-btn">🔍</button>
              </div>
              <div className="header-links">
                <a href="#" className="header-link">MY ACCOUNT</a>
                <a href="#" className="header-link">CART ({cartCount})</a>
                
                <a href="tel:+254 711 435629" className="header-link">Sales: +254 711 435629</a>
              </div>
            </div>
          </div>
        </div>
        <nav className="shop-nav">
          <div className="container">
            <ul className="nav-menu">
              <li><a href="#">Labels & Supplies</a></li>
              <li><a href="#">Mobile Computing</a></li>
              <li><a href="#">Barcode Scanning</a></li>
              <li><a href="#">Barcode Printing</a></li>
              <li><a href="#">ID Card Printing</a></li>
              <li><a href="#">POS & Touch Signage</a></li>
              <li><a href="#">Wireless Networking</a></li>
              <li><a href="#">RFID</a></li>
              <li><a href="#">Services</a></li>
              <li><a href="#">Brands</a></li>
            </ul>
          </div>
        </nav>
      </header>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="container">
          <div className="hero-content">
            <div className="hero-text">
              <h1>The Honeywell CT70: Powering Next-Gen Retail</h1>
              <p>Rugged, intuitive, and connected technology that helps your team work smarter and your customers shop better.</p>
              <button className="btn-hero">SHOP HONEYWELL CT70</button>
            </div>
            {/* <div className="hero-images">
              <img src="https://via.placeholder.com/300x400?text=CT70+Back" alt="Honeywell CT70 Back" />
              <img src="https://via.placeholder.com/300x400?text=CT70+Front" alt="Honeywell CT70 Front" />
            </div> */}
          </div>
        </div>
      </section>

      {/* Guarantees Section */}
      <section className="guarantees-section">
        <div className="container">
          <div className="guarantees-grid">
            <div className="guarantee-item">
              <div className="guarantee-icon">
                <AttachMoneyIcon sx={{ fontSize: 24, color: '#000' }} />
              </div>
              <h3>Price Match Guarantee</h3>
              <p>Within 30 days of your purchase</p>
            </div>
            <div className="guarantee-item">
              <div className="guarantee-icon">
                <BusinessCenterIcon sx={{ fontSize: 24, color: '#000' }} />
              </div>
              <h3>Sales Expertise Guarantee</h3>
              <p>Industry experts are standing by to answer your questions</p>
            </div>
            <div className="guarantee-item">
              <div className="guarantee-icon">
                <LocalShippingIcon sx={{ fontSize: 24, color: '#000' }} />
              </div>
              <h3>Same Day Delivery Guarantee</h3>
              <p>If it's in stock, it will ship same day</p>
            </div>
          </div>
        </div>
      </section>

      {/* Shop Top Categories */}
      <section className="categories-section">
        <div className="container">
          <h2 className="section-title">Shop Top Categories</h2>
          {categoriesError ? (
            <p className="category-hint" style={{ color: '#b45309', marginBottom: 12 }}>
              {categoriesError} — ensure ChesiPortal_API is running and categories are enabled with no parent in the Portal.
            </p>
          ) : null}
          <div className="categories-grid">
            {topCategories.length === 0 && !categoriesError ? (
              <p className="category-hint" style={{ gridColumn: '1 / -1' }}>
                No published categories yet. Add top-level categories in Chesi Portal (CMS → Categories) and mark them enabled.
              </p>
            ) : (
              topCategories.map((category) => {
                const IconComponent = FALLBACK_ICON_BY_SLUG[category.slug] || PhoneAndroidIcon;
                const origin = shopImageBase();
                const raw = category.icon_url;
                const imgSrc =
                  raw && /^https?:\/\//i.test(raw)
                    ? raw
                    : raw
                      ? `${origin}${raw.startsWith('/') ? '' : '/'}${raw}`
                      : null;
                return (
                  <Link key={category.id} to={`/shop/category/${category.slug}`} className="category-item">
                    <div className="category-icon">
                      {imgSrc ? (
                        <img src={imgSrc} alt="" style={{ maxWidth: 48, maxHeight: 48, objectFit: 'contain' }} />
                      ) : (
                        <IconComponent sx={{ fontSize: 48, color: '#000' }} />
                      )}
                    </div>
                    <p className="category-name">{category.name}</p>
                  </Link>
                );
              })
            )}
          </div>
        </div>
      </section>

      {/* Best Sellers */}
      <section className="products-section best-sellers-section">
        <div className="container">
          <h2 className="section-title">Best Sellers</h2>
          {shopProductsError ? (
            <p className="category-hint" style={{ color: '#b45309', marginBottom: 12 }}>
              {shopProductsError} — ensure ChesiPortal_API is running and products are published with an enabled category.
            </p>
          ) : null}
          <div className="products-grid">
            {bestSellers.length === 0 && !shopProductsError ? (
              <p className="category-hint" style={{ gridColumn: '1 / -1' }}>
                No published products yet. Add products in Chesi Portal (CMS → Products) and set status to Published.
              </p>
            ) : (
              bestSellers.map((product) => (
                <ProductCard key={product.id} product={product} variant="strip-card" />
              ))
            )}
          </div>
        </div>
      </section>

      {/* Easy Label Finder Banner */}
      <section className="label-finder-banner">
        <div className="container">
          <div className="banner-content">
            <div className="banner-text">
              <h2>Find the right barcode labels for your printer.</h2>
              <p>EASY LABEL FINDER! Most Labels Ship Same Day.</p>
              <button className="btn-banner">Try our Label Finder</button>
            </div>
            <div className="banner-image">
              <img src="https://via.placeholder.com/500x300?text=Label+Printer+Setup" alt="Label Finder" />
            </div>
          </div>
        </div>
      </section>

      {/* Brands Section */}
      <section className="brands-section">
        <div className="container">
          <div className="brands-header">
            <h2 className="section-title">brands</h2>
            <a href="#" className="link-more">SHOP BY BRAND &gt;</a>
          </div>
          {brandsError ? (
            <p className="category-hint" style={{ color: '#b45309', marginBottom: 12 }}>
              {brandsError} — ensure ChesiPortal_API is running and brands are enabled in Chesi Portal.
            </p>
          ) : null}
          <div className="brands-grid">
            {brands.length === 0 && !brandsError ? (
              <p className="category-hint" style={{ gridColumn: '1 / -1' }}>
                No enabled brands found. Create brands in Chesi Portal (CMS → Brands) and keep them enabled.
              </p>
            ) : (
              brands.map((brand) => {
                const origin = shopImageBase();
                const raw = brand.logo_url;
                const imgSrc =
                  raw && /^https?:\/\//i.test(raw)
                    ? raw
                    : raw
                      ? `${origin}${raw.startsWith('/') ? '' : '/'}${raw}`
                      : null;
                return (
                  <div key={brand.id || brand.slug || brand.name} className="brand-item" title={brand.name}>
                    {imgSrc ? (
                      <img src={imgSrc} alt={brand.name} />
                    ) : (
                      <span className="brand-name-fallback">{brand.name}</span>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      </section>

      {/* Customer Favorites */}
      <section className="products-section">
        <div className="container">
          <h2 className="section-title">Customer Favorites</h2>
          <div className="products-grid">
            {customerFavorites.length === 0 && !shopProductsError ? (
              <p className="category-hint" style={{ gridColumn: '1 / -1' }}>
                More products will appear here as your catalog grows (next items after Best Sellers).
              </p>
            ) : (
              customerFavorites.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))
            )}
          </div>
        </div>
      </section>

      {/* Testimonial Section */}
      <section className="testimonial-section">
        <div className="container">
          <div className="testimonial-content">
            <div className="testimonial-logo">
              <img src="https://via.placeholder.com/150x50?text=Trustpilot" alt="Trustpilot" />
              <div className="stars">★★★★★</div>
            </div>
            <div className="testimonial-quote">
              <p>"World Class! We love doing business with Chesi! Their staff is always friendly and knowledgeable, and willing to understand their customer's true needs in order to find the appropriate product(s) for their unique circumstances. Their pricing is very competitive, and shipping times are second to none. We are pleased with the partnership and will continue doing business with them for many years to come."</p>
              <p className="testimonial-author">- BENJAMIN</p>
            </div>
            <div className="testimonial-image">
              <img src="https://via.placeholder.com/200x200?text=Customer" alt="Customer" />
            </div>
          </div>
        </div>
      </section>

      {/* Top Customers */}
      <section className="top-customers-section">
        <div className="container">
          <h2 className="section-title">OUR TOP CUSTOMERS</h2>
          <div className="customers-grid">
            {topCustomers.map((customer, index) => (
              <div key={index} className="customer-item">
                <img src={customer.logo} alt={customer.name} />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Free Tools */}
      <section className="tools-section">
        <div className="container">
          <h2 className="section-title">free tools</h2>
          <div className="tools-grid">
            <div className="tool-item">
              <div className="tool-icon">🏷️</div>
              <h3>Custom Barcode Labels</h3>
            </div>
            <div className="tool-item">
              <div className="tool-icon">📋</div>
              <h3>Preprinted Barcode Labels</h3>
            </div>
            <div className="tool-item">
              <div className="tool-icon">🔧</div>
              <h3>Free Barcode Generator</h3>
            </div>
            <div className="tool-item">
              <div className="tool-icon">🔤</div>
              <h3>Free Barcode Font</h3>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="shop-footer">
        <div className="footer-top">
          <div className="container">
            <div className="footer-contact">
              <div className="contact-item">
                <span className="contact-icon">💬</span>
                <span>CHAT</span>
              </div>
              <div className="contact-item">
                <span className="contact-icon">✉️</span>
                <span>EMAIL US</span>
              </div>
              <div className="contact-item">
                <span className="contact-icon">📞</span>
                <span>+254 727 450523</span>
              </div>
            </div>
          </div>
        </div>
        <div className="footer-bottom">
          <div className="container">
            <div className="footer-grid">
              <div className="footer-column">
                <h4>Our Company</h4>
                <ul>
                  <li><a href="#">About Us</a></li>
                  <li><a href="#">Careers</a></li>
                  <li><a href="#">Contact</a></li>
                </ul>
              </div>
              <div className="footer-column">
                <h4>Help & Support</h4>
                <ul>
                  <li><a href="#">FAQs</a></li>
                  <li><a href="#">Shipping</a></li>
                  <li><a href="#">Returns</a></li>
                </ul>
              </div>
              <div className="footer-column">
                <h4>Services & Solutions</h4>
                <ul>
                  <li><a href="#">Custom Solutions</a></li>
                  <li><a href="#">Technical Support</a></li>
                  <li><a href="#">Training</a></li>
                </ul>
              </div>
              <div className="footer-column">
                <h4>Shop Now</h4>
                <ul>
                  <li><a href="#">All Products</a></li>
                  <li><a href="#">New Arrivals</a></li>
                  <li><a href="#">Sale Items</a></li>
                </ul>
              </div>
            </div>
            <div className="footer-newsletter">
              <h4>Stay Informed</h4>
              <div className="newsletter-form">
                <input type="email" placeholder="Enter your email" />
                <button>SUBMIT</button>
              </div>
            </div>
            <div className="footer-social">
              <a href="#">LinkedIn</a>
              <a href="#">Twitter</a>
              <a href="#">Facebook</a>
              <a href="#">YouTube</a>
            </div>
            <div className="footer-copyright">
              <p>&copy; 2025 Chesi Mobile Solutions. All rights reserved.</p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Shop;

